import React, { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { Box } from '../../legacy/Box';
import { identifyComponent } from '../../util';
import { Text } from '../Text';

type NativeLabelAttributes = React.ComponentPropsWithoutRef<'label'>;

export type InputLabelSize = 'x-small' | 'small' | 'medium' | 'large';

const fontSizeMapping = {
  'x-small': 'bodySmall',
  small: 'bodySmall',
  medium: 'bodyMedium',
  large: 'bodyLarge',
} as const;

// one step down from above font size main label text mapping, less x-small and small
const optionalFontSizeMapping = {
  'x-small': 'bodySmall',
  small: 'bodySmall',
  medium: 'bodySmall',
  large: 'bodyMedium',
} as const;

export interface InputLabelProps extends NativeLabelAttributes {
  children: ReactNode;
  /**
   * If `true`, the input label will not be able to be selected or clicked.
   */
  disabled?: boolean;
  /**
   * If `true`, then no margin will be applied to the bottom of the label.
   */
  disableMargin?: boolean;
  /**
   * If `true`, will append (optional) text
   * Default false.
   */
  optional?: boolean;
  /**
   * If `true`, will append an asterisk to the end of the label
   * Default false.
   */
  required?: boolean;
  /**
   * Size of the input label - defaults to `medium`.
   */
  size?: InputLabelSize;
}

export const InputLabel: React.FC<InputLabelProps> = ({
  children,
  disabled = false,
  disableMargin = false,
  optional = false,
  required = false,
  size = 'medium',
  ...rest
}) => {
  const fontSize = fontSizeMapping[size];
  return (
    <Box
      data-component="InputLabel"
      marginBottom={disableMargin ? undefined : 4}
      {...identifyComponent('input-label')}
    >
      <label {...rest}>
        <Text appearance={fontSize} color="emphasized" tag="span">
          {children}
          {required ? ' *' : null}
        </Text>
        {optional && (
          <Box paddingLeft={8} inline>
            <Text appearance={optionalFontSizeMapping[size]} color="secondary" tag="span">
              <FormattedMessage defaultMessage="Optional" id="InWqys" />
            </Text>
          </Box>
        )}
      </label>
    </Box>
  );
};
